import "./SubmissionBuilder.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import CmsSubmissionModal from "components/Documents/cms-submission/CmsSubmissionModal";
import TableCellStatusPill from "components/TableCellStatusPill/TableCellStatusPill";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useState } from "react";
import type { MSADataType } from "types/DataTypes";
import { formatCurrency } from "utils/format-utils";
import { useConstants } from "utils/hooks";

import {
  AUTO_GENERATE_ITEMS,
  CHECKLIST_STATUS_LABEL,
  COVER_LETTER_DATA_ROWS,
  GATE_RULES,
  REQUIREMENT_LABEL,
  SUBMISSION_CHECKLIST,
  serviceTypeHasSubmissionBuilder,
  type ChecklistItemStatus,
  type ThresholdCheckRow,
} from "./submission-builder-skeleton";

type SubmissionBuilderProps = {
  msa: MSADataType;
};

const STATUS_ICON: Record<ChecklistItemStatus, string> = {
  complete: "check_circle",
  needs_review: "error",
  missing: "cancel",
  not_applicable: "remove_circle",
  pending: "radio_button_unchecked",
};

const STATUS_PILL_VARIANT: Record<
  ChecklistItemStatus,
  "success" | "warning" | "error" | "neutral" | "info"
> = {
  complete: "success",
  needs_review: "warning",
  missing: "error",
  not_applicable: "neutral",
  pending: "info",
};

const yesNo = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return value ? "Yes" : "No";
};

const thresholdRowsFor = (msa: MSADataType): ThresholdCheckRow[] => [
  {
    label: "Medicare entitled?",
    value: yesNo(msa.claimant?.is_medicare_eligible),
  },
  {
    label: "Reasonable expectation",
    value: yesNo(msa.claimant?.reasonable_expectation),
  },
  { label: "Query result on file", value: "—" },
  {
    label: "Total settlement (gross)",
    value: msa.file?.estimated_settlement_amount
      ? formatCurrency(msa.file.estimated_settlement_amount)
      : "—",
  },
  { label: "Applicable threshold", value: "—" },
  { label: "Result", value: "Not evaluated" },
];

/**
 * Submission Builder — readiness view for the CMS Submission tab.
 *
 * This is the skeleton: layout, sections and the CMS reference-guide checklist
 * are in place, but nothing is computed yet. Every checklist row reports
 * "Not evaluated", Request Missing Items is disabled, and the counts in the
 * readiness banner are placeholders. Readiness computation and document
 * linking each land separately.
 */
export const SubmissionBuilder = ({ msa }: SubmissionBuilderProps) => {
  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const fileId = msa.file?.id;
  const claimantName = [msa.claimant?.last_name, msa.claimant?.first_name]
    .filter(Boolean)
    .join(", ");
  const requiredCount = SUBMISSION_CHECKLIST.filter(
    (item) => item.requirement === "required",
  ).length;
  // Skeleton placeholder. Once readiness is computed the unsubmitted label
  // splits into e.g. "Ready", "Gated" and "Threshold not met".
  const submissionStatus = msa.has_been_submitted_to_cms
    ? "Submitted"
    : "Not Submitted — Gated";
  const thresholdRows = thresholdRowsFor(msa);

  return (
    <section
      className="submission-builder"
      data-testid="submission-builder"
      aria-label="Submission Builder"
    >
      <div className="submission-builder__banner data-block max-content-w">
        <div className="submission-builder__banner-row">
          <div className="submission-builder__banner-title">
            <span className="submission-builder__heading">
              Submission Builder — Readiness
            </span>
            <span className="submission-builder__subheading">
              {claimantName && <>{claimantName} · </>}
              File #{msa.file?.id} · {msa.type}
            </span>
          </div>
          <div className="submission-builder__banner-actions">
            <TableCellStatusPill
              label={`Submission Status: ${submissionStatus}`}
              variant={msa.has_been_submitted_to_cms ? "success" : "warning"}
            />
            <div className="submission-builder__buttons">
              <Button
                className="submission-builder__button"
                disabled={!fileId}
                onClick={() => setIsPackageModalOpen(true)}
              >
                Generate Submission Package
              </Button>
              <Button
                className="submission-builder__button"
                disabled
                title="Requesting missing items is not available yet"
              >
                Request Missing Items
              </Button>
            </div>
          </div>
        </div>
        <div
          className="submission-builder__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={requiredCount}
          aria-valuenow={0}
          aria-valuetext={`0 of ${requiredCount} required items complete`}
        >
          <div
            className="submission-builder__progress-fill"
            style={{ width: "0%" }}
          />
        </div>
        <div className="submission-builder__summary">
          <strong>0 of {requiredCount} required items complete</strong> ·
          readiness not yet evaluated · Gate releases when all required items
          are green or overridden by Legal/Compliance.
        </div>
      </div>

      <div className="submission-builder__columns">
        <div className="submission-builder__column">
          <Card heading="Threshold Check — Gate 1" type="inverse">
            <dl className="submission-builder__kv">
              {thresholdRows.map((row) => (
                <div key={row.label} className="submission-builder__kv-row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="submission-builder__note">
              Gross lifetime payout, not net of fees/liens. Annuities valued at
              lifetime payout. Ref. Guide 10.1 E.1 / Sec. 8.1
            </p>
          </Card>

          <Card heading="Cover Letter Data Completeness" type="inverse">
            <dl className="submission-builder__kv">
              {COVER_LETTER_DATA_ROWS.map((row) => (
                <div key={row.label} className="submission-builder__kv-row">
                  <dt>{row.label}</dt>
                  <dd>
                    <TableCellStatusPill
                      label={CHECKLIST_STATUS_LABEL[row.status]}
                      variant={STATUS_PILL_VARIANT[row.status]}
                    />
                  </dd>
                </div>
              ))}
            </dl>
            <p className="submission-builder__note">
              Drives the auto-generated Submission Letter. Any red blocks
              generation.
            </p>
          </Card>
        </div>

        <div className="submission-builder__column submission-builder__column--wide">
          <Card
            heading={
              <span className="submission-builder__checklist-heading">
                <span>Submission Checklist</span>
                <span className="submission-builder__checklist-hint">
                  CMS Sec. # = file-name prefix · Ref. Guide Table 10-1
                </span>
              </span>
            }
            type="inverse"
          >
            <ol className="submission-builder__checklist">
              {SUBMISSION_CHECKLIST.map((item, index) => (
                <li
                  // several rows share a section prefix (five "50"s, two
                  // "40"s), so the index is part of the key
                  key={`${item.section}-${index}`}
                  className={`submission-builder__item submission-builder__item--${item.status}`}
                >
                  <span
                    className="material-icons submission-builder__item-icon"
                    aria-hidden="true"
                  >
                    {STATUS_ICON[item.status]}
                  </span>
                  <span className="submission-builder__item-section">
                    {item.section}
                  </span>
                  <span className="submission-builder__item-body">
                    <span className="submission-builder__item-title">
                      {item.title}
                    </span>
                    <span className="submission-builder__item-description">
                      {REQUIREMENT_LABEL[item.requirement]} · {item.description}
                    </span>
                  </span>
                  <span className="submission-builder__item-status">
                    <TableCellStatusPill
                      label={CHECKLIST_STATUS_LABEL[item.status]}
                      variant={STATUS_PILL_VARIANT[item.status]}
                    />
                  </span>
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <div className="submission-builder__column">
          <Card heading="Auto-Generate" type="inverse">
            <p className="submission-builder__note">
              Produced by KAboodle from file data — no manual drafting:
            </p>
            <dl className="submission-builder__kv">
              {AUTO_GENERATE_ITEMS.map((item, index) => (
                <div
                  key={`${item.section}-${index}`}
                  className="submission-builder__kv-row"
                >
                  <dt>
                    {item.section} {item.title}
                  </dt>
                  <dd>
                    <TableCellStatusPill label="Pending" variant="neutral" />
                  </dd>
                </div>
              ))}
            </dl>
          </Card>

          <Card heading="Package Preview" type="inverse">
            <p className="submission-builder__note">
              CMS naming &amp; order, PDF/A, bookmarked index. The package
              listing appears once readiness is computed.
            </p>
            <p className="submission-builder__note">
              Two-digit section prefix per Ref. Guide 10.0 — reduces errors and
              development requests.
            </p>
          </Card>

          <Card
            heading="Gate rules"
            type="inverse"
            classNames="submission-builder__gate-rules"
          >
            <ul className="submission-builder__rules">
              {GATE_RULES.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
      {isPackageModalOpen && fileId && (
        <CmsSubmissionModal
          fileId={fileId}
          onClose={() => setIsPackageModalOpen(false)}
        />
      )}
    </section>
  );
};

/**
 * Mounts the Submission Builder only when its single feature flag is on and
 * the service is an MSA or Amended Review. Renders nothing otherwise, so the
 * rest of the CMS Submission tab is untouched when the flag is off.
 */
const SubmissionBuilderGate = ({ msa }: SubmissionBuilderProps) => {
  const { constants } = useConstants();
  const enabled = Boolean(
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.CMS_SUBMISSION_BUILDER_ENABLED],
  );

  if (!enabled || !serviceTypeHasSubmissionBuilder(msa?.type)) {
    return null;
  }

  return <SubmissionBuilder msa={msa} />;
};

export default SubmissionBuilderGate;
